// MAVEN'S SETUP DOMAIN (CR-20260910-001 milestone 4, lane S6).
//
// Frontend-owned and pure TypeScript (no Angular), so the invariants can be unit-tested directly.
// This is live session state, never serialized as a template. templates/maven-settings-template
// defines the separate reusable subset (2026-10-05): relative dates, hedge preferences and an
// explicit all-dates indicator override. Portfolio ids, hedge ids and fixed dates/strikes stay here.
//
// The DATE half is not re-declared here. It is `RollPositionsInputs`, the type the APG ORION's
// questionnaire already emits, imported as a TYPE — no module change, no copy. Reusing the type
// is what makes Maven's date rules identical to the APG's BY CONSTRUCTION rather than by
// intention: the Monday default, the per-date un-exclude, the specific-date carve-outs and the
// forward-only End all arrive in the shape that component produces. Its `bias` member is the S10
// placeholder and reaches nothing on the wire.
//
// ES5 note: arrays + indexOf only (project rule).

import {RollPositionsInputs} from '../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {MarketConditionOverrides} from '../copilot-shared/opg-copilot-domain';

// S4. The ONLY action on existing hedges — there is no Adjust option in iteration 1 (ruling E3),
// and the choice never touches the new-hedge parameters, which are always asked.
export type MavenExistingHedges = 'close' | 'ignore';

export const MAVEN_EXISTING_HEDGES: MavenExistingHedges[] = ['close', 'ignore'];

// CR-20260916-003 (owner screencast 2026-09-16): under Close, WHICH hedges. The toggled
// expirations ('yyyy-MM-dd') and the chosen hedges by saved-book GROUP ID. THE HEDGE IS THE UNIT
// (ruling 9): a calendar hedge — legs on more than one expiration — is one id, selected in every
// expiration box it appears in and closed whole. Both lists empty under Ignore, and an empty
// list under Close is an error at Apply (ruling 3), never "close nothing".
export interface MavenCloseSelection {
    expirations: string[];
    hedgeIds: string[];
}

export function emptyMavenCloseSelection(): MavenCloseSelection {
    return {expirations: [], hedgeIds: []};
}

// S8. THREE kinds. "Both" is not offered (ruling Q-M2), and neither is a start: every kind starts
// from that side's anchor — the paired adjustment solution's short strike — plus that side's
// offset, which the trader never enters and the panel never knows before the run.
export type MavenCoverageKind = 'range' | 'followThrough' | 'expectedRange';

export const MAVEN_COVERAGE_KINDS: MavenCoverageKind[] = ['range', 'followThrough', 'expectedRange'];

export type MavenRangeMode = 'endStrike' | 'distance';

export interface MavenCoverage {
    kind: MavenCoverageKind;
    // Missing mode is the legacy fixed-end workflow. Only the selected range value is consumed.
    rangeMode?: MavenRangeMode;
    rangeSize?: number | null;
    endStrike: number | null;
}

export function mavenRangeMode(coverage: MavenCoverage): MavenRangeMode {
    return coverage && coverage.rangeMode ? coverage.rangeMode : 'endStrike';
}

// S9. EXACTLY the four the ORION (OPG) offers (ruling Q-M1). Never High, never Conservative,
// never Auto — those belong to other controls in this platform and a fifth value here would be a
// contract violation, not a nicety.
export type MavenRiskLevel = 'Low' | 'Moderate' | 'Aggressive' | 'All';

export const MAVEN_RISK_LEVELS: MavenRiskLevel[] = ['Low', 'Moderate', 'Aggressive', 'All'];

// Kind-discriminated: a dollar cap OR a relative level, never both. `normalized` belongs to the
// dollar variant only and is a GUI meaning, not a wire field — the amount crosses as plain
// dollars either way (see maven-run-client.ts).
export type MavenMaxRisk =
    | { kind: 'dollar'; amount: number | null; normalized: boolean }
    | { kind: 'relative'; level: MavenRiskLevel | null };

// S6. The cushion the trader wants from hedges, per side, entered EITHER normalized (per default
// quantity) or raw dollars — the same two modes the ORION (OPG) offers. Unrelated to the
// adjustment credit.
export interface MavenMinProfit {
    amount: number | null;
    normalized: boolean;
}

// S5-S9, one side. `enabled` is explicit rather than inferred from whether the fields happen to
// be filled: "the trader has not opened this side" and "the trader opened it and left it half
// done" are different states, and only the second is an error worth a message. Both sides
// disabled is invalid and the run is refused (ruling Q7).
export interface MavenSideInputs {
    mode?: 'custom' | 'preset';
    presetId?: string | null;
    presetValid?: boolean;
    presetName?: string | null;
    enabled: boolean;
    // S7, in POINTS. The CALL offset is measured from the short CALL and the PUT offset from the
    // short PUT of the paired adjustment solution's after-state.
    offsetFromShorts: number | null;
    coverage: MavenCoverage;
    minProfit: MavenMinProfit;
    maxRisk: MavenMaxRisk;
}

// The whole Setup. Three IDS and nothing that identifies state: the saved positions, the saved
// hedge book, the template body and the expected-range model are read on the BACKEND by id, for
// the portfolio's OWNER (A5, ruling R-E).
export interface MavenSetupInputs {
    portfolioId: string | null;
    portfolioOwnerId: string | null;
    templateId: string | null;
    dates: RollPositionsInputs;
    existingHedges: MavenExistingHedges;
    // CR-20260916-003: the Close selection. Read only when existingHedges is 'close'.
    closeSelection: MavenCloseSelection;
    calls: MavenSideInputs;
    puts: MavenSideInputs;
    // CR-20260915-001. The trader's per-date market-condition overrides (IV, vertical skews,
    // horizontal skew), keyed by 'yyyy-MM-dd' — the ORION (OPG)'s own record type, reused as a
    // TYPE. Holds EDITED dates only (a seeded reading is display, never an override), and is
    // EMPTIED whenever the dates the run targets change (owner ruling 2026-09-15, #4). The Setup
    // component owns the map; the block under "Override Market Indicators" is a view over it.
    marketOverrides: MarketConditionOverrides;
}

// A side as it starts: closed, with fixed-end Range coverage waiting for its strike and a relative
// risk level unchosen. A fresh object every call — a shared literal would be mutated in place by
// the questionnaire and leak between sides.
export function emptyMavenSide(): MavenSideInputs {
    return {
        enabled: false,
        offsetFromShorts: null,
        coverage: {kind: 'range', rangeMode: 'endStrike', rangeSize: null, endStrike: null},
        minProfit: {amount: null, normalized: true},
        maxRisk: {kind: 'relative', level: null}
    };
}

// The Setup as the panel opens, and as Reset returns it. `dates` is filled in by the questionnaire
// component, which owns the Monday default and the start anchor; this seeds it EMPTY rather than
// guessing, because a seeded date the trader did not choose is a date he did not choose.
export function emptyMavenSetup(dates: RollPositionsInputs): MavenSetupInputs {
    return {
        portfolioId: null,
        portfolioOwnerId: null,
        templateId: null,
        dates: dates,
        // Close is NOT the default. Closing hedges costs money and sends an order; the safe
        // starting point is the one that changes nothing.
        existingHedges: 'ignore',
        closeSelection: emptyMavenCloseSelection(),
        calls: emptyMavenSide(),
        puts: emptyMavenSide(),
        marketOverrides: {}
    };
}

// ---- Run state (session only) ---------------------------------------------------------------

// 'pending' is frontend-only; the other five are the backend's closed outcome set. There is no
// 'cancelled': Maven has no stop button and no cancel command (ruling R-D), so the only way a run
// ends early is 'superseded' — the trader pressed Apply again.
export type MavenRunOutcome =
    | 'pending'
    | 'succeeded'
    | 'empty'
    | 'superseded'
    | 'preSendBlocked'
    | 'terminalFailed';

export interface MavenRunStage {
    stage: string;
    detail: string | null;
    at: number;
}

export interface MavenRun {
    id: string;
    startedAt: number;
    endedAt: number | null;
    outcome: MavenRunOutcome;
    // The stages seen so far, in arrival order. Shown while the run works; replaced by the cards
    // in ONE reveal when the delivery lands.
    stages: MavenRunStage[];
    failureDetail: string | null;
    // The Existing Hedges choice the run was SENT with (owner, vrequest 2026-09-13, third pass).
    // Snapshotted at Apply: the trader may flip the Setup toggle while the run works, and the
    // cards must describe the run that produced them, not the toggle's current state.
    existingHedges: MavenExistingHedges;
    // CR-20260916-003: the close selection the run was SENT with, snapshotted the same way.
    closeSelection: MavenCloseSelection;
}
